import { useCallback, useMemo, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import {
  GET_TRUST_CENTER,
  PUBLISH_TRUST_CENTER,
  SAVE_TRUST_CENTER_ACCESS,
  SAVE_TRUST_CENTER_CATEGORIES,
  SAVE_TRUST_CENTER_CONTROL_VISIBILITY,
  SAVE_TRUST_CENTER_FAQS,
  SAVE_TRUST_CENTER_FRAMEWORKS,
  SAVE_TRUST_CENTER_OVERVIEW,
  SAVE_TRUST_CENTER_RESOURCES,
  SAVE_TRUST_CENTER_SUBPROCESSORS,
  SAVE_TRUST_CENTER_UPDATES,
  SAVE_TRUST_CENTER_APPROVERS,
  UNPUBLISH_TRUST_CENTER,
  GET_TRUST_CENTER_DOWNLOAD,
  GET_TRUST_CENTER_ACTIVITY,
  CONNECT_TRUST_CENTER_DOMAIN,
  CHECK_TRUST_CENTER_DOMAIN,
  REMOVE_TRUST_CENTER_DOMAIN,
} from "../../../apis/trust-center-apis/trustCenterApis";
import { sectionsFor, toAdminView } from "../data/adminView";

const MUTATIONS = {
  overview: [SAVE_TRUST_CENTER_OVERVIEW, "saveTrustCenterOverview"],
  access: [SAVE_TRUST_CENTER_ACCESS, "saveTrustCenterAccess"],
  categories: [
    SAVE_TRUST_CENTER_CATEGORIES,
    "saveTrustCenterResourceCategories",
  ],
  resources: [SAVE_TRUST_CENTER_RESOURCES, "saveTrustCenterResources"],
  frameworks: [SAVE_TRUST_CENTER_FRAMEWORKS, "saveTrustCenterFrameworks"],
  subprocessors: [
    SAVE_TRUST_CENTER_SUBPROCESSORS,
    "saveTrustCenterSubprocessors",
  ],
  faqs: [SAVE_TRUST_CENTER_FAQS, "saveTrustCenterFaqs"],
  updates: [SAVE_TRUST_CENTER_UPDATES, "saveTrustCenterUpdates"],
  approvers: [SAVE_TRUST_CENTER_APPROVERS, "saveTrustCenterApprovers"],
  controls: [
    SAVE_TRUST_CENTER_CONTROL_VISIBILITY,
    "saveTrustCenterControlVisibility",
  ],
};

const errorMessage = (err) =>
  err?.graphQLErrors?.[0]?.message ||
  err?.message ||
  "Something went wrong. Try again.";

// Admin-side state for the Trust Center editor.
// update(changes) shows the change at once, saves each changed section to
// the API (one at a time, in order) and then shows what the server stored.
// It resolves to true when everything saved; on failure the change is undone
// and onError gets the reason.
export const useTrustCenterAdmin = ({ onError } = {}) => {
  const client = useApolloClient();
  const { data, loading, error, refetch } = useQuery(GET_TRUST_CENTER, {
    fetchPolicy: "cache-and-network",
  });
  // Shown while saves are running.
  const [optimistic, setOptimistic] = useState(null);
  // Custom-domain choices made before the first publish.
  const [draftPublishing, setDraftPublishing] = useState(null);
  const queue = useRef(Promise.resolve());
  const pending = useRef(0);
  // True while any save is on its way to the server (for a loader).
  const [saving, setSaving] = useState(false);

  const server = data?.getTrustCenter;
  // The newest answer from the server: the query's, or a save's.
  const lastServer = useRef(null);
  if (server && server !== lastServer.current?.fromQuery)
    lastServer.current = { value: server, fromQuery: server };
  const view = useMemo(
    () => (server ? toAdminView(server, draftPublishing) : null),
    [server, draftPublishing]
  );
  const current = optimistic || view;
  const latest = useRef(current);
  latest.current = current;

  const store = useCallback(
    (saved) => {
      lastServer.current = { value: saved, fromQuery: server };
      client.writeQuery({
        query: GET_TRUST_CENTER,
        data: { getTrustCenter: saved },
      });
    },
    [client, server]
  );

  const enqueue = useCallback(
    (work) => {
      pending.current += 1;
      setSaving(true);
      const run = async () => {
        try {
          await work();
          return true;
        } catch (err) {
          setOptimistic(null);
          onError?.(errorMessage(err));
          refetch().catch(() => {});
          return false;
        } finally {
          pending.current -= 1;
          if (pending.current === 0) {
            setOptimistic(null);
            setSaving(false);
          }
        }
      };
      const result = queue.current.then(run);
      queue.current = result;
      return result;
    },
    [onError, refetch]
  );

  // Puts the draft live. `includeChanges` false: only where the page lives
  // changes (Settings → Custom domain); the content stays as published.
  const publish = useCallback(
    ({ target, customDomain, includeChanges = true }) =>
      enqueue(async () => {
        await client.mutate({
          mutation: PUBLISH_TRUST_CENTER,
          variables: {
            input: {
              target,
              customDomain: target === "custom" ? customDomain : null,
              acknowledged: true,
              includeChanges,
            },
          },
        });
        setDraftPublishing(null);
        await refetch();
        await client.refetchQueries({ include: [GET_TRUST_CENTER_ACTIVITY] });
      }),
    [client, enqueue, refetch]
  );

  const unpublish = useCallback(
    () =>
      enqueue(async () => {
        await client.mutate({ mutation: UNPUBLISH_TRUST_CENTER });
        await refetch();
        await client.refetchQueries({ include: [GET_TRUST_CENTER_ACTIVITY] });
      }),
    [client, enqueue, refetch]
  );

  const update = useCallback(
    (changes) => {
      const base = latest.current;
      if (!base) return Promise.resolve(false);
      const delta = typeof changes === "function" ? changes(base) : changes;
      const next = { ...base, ...delta };
      latest.current = next;

      // Settings → Custom domain. Before the first publish the choice waits
      // for the Publish dialog; once published, a new domain is published
      // straight away (the admin acknowledged the terms when publishing).
      if ("publishing" in delta) {
        const before = base.publishing;
        const after = { ...before, ...delta.publishing };
        const moved =
          after.target !== before.target ||
          after.customDomain !== before.customDomain;
        if (!base.isPublished || !moved) {
          setDraftPublishing(after);
          return Promise.resolve(true);
        }
        setOptimistic(next);
        return publish({ ...after, includeChanges: false });
      }

      const sections = sectionsFor(delta);
      if (!sections.length) return Promise.resolve(true);
      setOptimistic(next);
      return enqueue(async () => {
        let saved = null;
        for (const section of sections) {
          const [mutation, field] = MUTATIONS[section.id];
          const result = await client.mutate({
            mutation,
            variables: section.variables(next),
          });
          saved = result.data[field];
        }
        store(saved);
        // Nothing else queued: the next update builds on what the server
        // stored (real ids), without waiting for a re-render.
        if (pending.current === 1)
          latest.current = toAdminView(saved, draftPublishing);
      });
    },
    [client, enqueue, publish, store, draftPublishing]
  );

  // Settings → Custom domain: saved on the server straight away (also before
  // the first publish), which looks the DNS record up and stores the status.
  const runDomain = useCallback(
    (mutation, field, variables) =>
      enqueue(async () => {
        const result = await client.mutate({ mutation, variables });
        setDraftPublishing(null);
        store(result.data[field]);
      }),
    [client, enqueue, store]
  );
  const connectDomain = useCallback(
    (domain) =>
      runDomain(CONNECT_TRUST_CENTER_DOMAIN, "connectTrustCenterDomain", {
        domain,
      }),
    [runDomain]
  );
  const checkDomain = useCallback(
    () => runDomain(CHECK_TRUST_CENTER_DOMAIN, "checkTrustCenterDomain"),
    [runDomain]
  );
  const removeDomain = useCallback(
    () => runDomain(REMOVE_TRUST_CENTER_DOMAIN, "removeTrustCenterDomain"),
    [runDomain]
  );

  // Card refresh buttons: saves finish first, so a refresh never brings back
  // data from before a save. `queries` are other queries the card reads.
  const refresh = useCallback(
    async (queries = []) => {
      await queue.current;
      await client.refetchQueries({ include: [GET_TRUST_CENTER, ...queries] });
    },
    [client]
  );

  // Signed link to a resource's file (any visibility); `view`: a link to show
  // it in the page instead. Rejects with the server's message.
  const download = useCallback(
    async (resourceId, { view = false } = {}) => {
      try {
        const result = await client.query({
          query: GET_TRUST_CENTER_DOWNLOAD,
          variables: { resourceId, view },
          fetchPolicy: "no-cache",
        });
        return result.data.getTrustCenterDownload;
      } catch (err) {
        throw new Error(errorMessage(err));
      }
    },
    [client]
  );

  // What the server last returned (real ids for rows just saved), in the
  // shape the screens use.
  const stored = useCallback(
    () =>
      lastServer.current
        ? toAdminView(lastServer.current.value, draftPublishing)
        : null,
    [draftPublishing]
  );

  return {
    data: current,
    stored,
    loading: loading && !server,
    saving,
    refresh,
    download,
    error,
    update,
    publish,
    unpublish,
    connectDomain,
    checkDomain,
    removeDomain,
  };
};

// Upsert / remove helpers for the list sections. New rows get a temporary id
// (not a database id) so the lists can key them until the save returns.
export const upsertItem = (list, item) =>
  item.id
    ? list.map((existing) =>
        existing.id === item.id ? { ...existing, ...item } : existing
      )
    : [...list, { ...item, id: `new-${Date.now()}-${list.length}` }];

export const removeItem = (list, id) => list.filter((item) => item.id !== id);
